// @flow
import { t } from '@lingui/macro';
import lodashGet from 'lodash-es/get';
import moment from 'moment';

import { I18N_FORMAT_MAP } from '@rover/kibble/styles';
import petFactory from '@rover/react-lib/factories/petFactory';
import type {
  ExpressParams,
  FetchPetImageResponse,
  Image,
  Pet,
  PetDetailsOptions,
} from '@rover/react-lib/types';
import { DEFAULT_LANG } from '@rover/shared/js/constants/i18n/language';
import { fullApiUrl } from '@rover/utilities/apiUrl';
import { getAxiosInstance } from '@rover/utilities/axiosUtils';
import { camelize, snakeify } from '@rover/utilities/object-utilities';

import DefaultAPIErrorHandler from './DefaultAPIErrorHandler';
import { get as personSummaryGet } from './personSummary.api';

export const PET_LIST_URL = fullApiUrl('pets/');
export const PERSON_SUMMARY_URL = fullApiUrl('me/');

export const scrubAPetData = (
  rawPet: $Shape<Pet> & { birthday: $PropertyType<Pet, 'birthday'> }
): Pet => {
  const ageYears = moment().diff(rawPet.birthday, 'years');
  const ageMonths = moment().diff(rawPet.birthday, 'months') % 12;

  return petFactory({
    ...rawPet,
    ageYears,
    ageMonths,
  });
};

export const scrubRawData = (
  rawPetsData: Array<$Shape<Pet> & { birthday: $PropertyType<Pet, 'birthday'> }>
): Array<Pet> => rawPetsData.map(scrubAPetData);

// Gets the current viewer's PersonSummary (/me API)
export const get = async (expressParams: ?ExpressParams): Promise<Array<Pet>> => {
  try {
    const data = await personSummaryGet(undefined, expressParams);
    return scrubRawData(data.pets);
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error while fetching pets.`);
  }
};

export const getMemberPets = async (
  memberUrl: string,
  expressParams: ?ExpressParams
): Promise<{ pets: Array<Pet>, memberName: string }> => {
  try {
    const data = await personSummaryGet(memberUrl, expressParams);
    return { pets: scrubRawData(data.pets), memberName: data.shortName };
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error while fetching pets.`);
  }
};

export const getDetail = async (petUrl: string, expressParams: ?ExpressParams): Promise<Pet> => {
  const ai = getAxiosInstance(expressParams);

  try {
    const { data } = await ai.get(petUrl);
    return scrubAPetData(camelize(data, true));
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error fetching pet details.`);
  }
};

export const getPetDetailsOptions = async (
  expressParams: ?ExpressParams
): Promise<PetDetailsOptions> => {
  const ai = getAxiosInstance(expressParams);
  const petDetailsOptionsUrl = fullApiUrl('pets/');

  try {
    const response = await ai.request({ url: petDetailsOptionsUrl, method: 'options' });
    const petDetailsOptions = lodashGet(response, 'data.actions.POST.pet_details.child.children');
    return camelize(petDetailsOptions, true);
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error fetching pet details options.`);
  }
};

export const deletePet = async (
  petUrl: string,
  expressParams: ?ExpressParams
): Promise<boolean> => {
  const ai = getAxiosInstance(expressParams);

  try {
    await ai.delete(petUrl);
    return true;
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error deleting pet.`);
  }
};

export const memorializePet = async (
  petUrl: string,
  memorialDate: ?Date,
  requestingSupport: boolean,
  expressParams: ?ExpressParams
): Promise<boolean> => {
  const ai = getAxiosInstance(expressParams);

  try {
    await ai.put(petUrl, {
      is_memorialized: true,
      memorial_date: memorialDate,
      // requesting_support: requestingSupport,
    });
    return true;
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error memorializing pet.`);
  }
};

export const getImages = async (
  url: string,
  pageSize?: number,
  expressParams: ?ExpressParams
): Promise<FetchPetImageResponse> => {
  const ai = getAxiosInstance(expressParams);

  try {
    const { data } = await ai.get(url, { params: { page_size: pageSize } });
    return camelize(data, true);
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error fetching pet images.`);
  }
};

export const deleteImage = async (
  imageUrl: string,
  expressParams: ?ExpressParams
): Promise<boolean> => {
  const ai = getAxiosInstance(expressParams);

  try {
    await ai.delete(imageUrl);
    return true;
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error deleting pet image.`);
  }
};

export const togglePublicPrivateImage = async (
  togglePrivacyUrl: string,
  imageId: string,
  expressParams: ?ExpressParams
): Promise<boolean> => {
  const ai = getAxiosInstance(expressParams);

  try {
    await ai.post(togglePrivacyUrl, {
      image_id: imageId,
    });
    return true;
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error setting public/private image.`);
  }
};

export const addImage = async (
  petImagesUrl: string,
  imageUrl: string,
  expressParams: ?ExpressParams
): Promise<Image> => {
  const ai = getAxiosInstance(expressParams);

  try {
    const response = await ai.post(petImagesUrl, {
      image_url: imageUrl,
    });
    return camelize(response.data, true);
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error adding a pet image.`);
  }
};

export const setImageToFirst = async (
  orderingUrl: string,
  expressParams: ?ExpressParams
): Promise<void> => {
  const ai = getAxiosInstance(expressParams);

  try {
    ai.put(orderingUrl, {
      previous: null,
    });
  } catch (e) {
    throw DefaultAPIErrorHandler(e, t`There was an error updating the pet image.`);
  }
};

export const postOrPut = async (pet: $Shape<Pet>, expressParams: ?ExpressParams): Promise<Pet> => {
  const ai = getAxiosInstance(expressParams);
  const birthday = moment()
    .subtract(pet.ageYears, 'years')
    .subtract(pet.ageMonths, 'months')
    .format(I18N_FORMAT_MAP[DEFAULT_LANG].DATE_ISO);
  let rawData = {
    ...pet,
    birthday,
  };

  rawData = Object.keys(rawData)
    .filter((k) => rawData[k] !== null)
    .reduce((obj, k) => {
      obj[k] = rawData[k]; // eslint-disable-line no-param-reassign
      return obj;
    }, {});

  try {
    const snakeData = snakeify(rawData, true);
    const { data } = await ai({
      method: pet.url ? 'PUT' : 'POST',
      url: pet.url ? pet.url : PET_LIST_URL,
      data: {
        ...snakeData,
      },
    });
    return scrubAPetData(camelize(data, true));
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error saving your pet information.`);
  }
};

export const postPetMetricNewSSU = async (
  pet: $Shape<Pet>,
  metricUrl: string,
  expressParams: ?ExpressParams
): Promise<void> => {
  const ai = getAxiosInstance(expressParams);
  const data = snakeify(pet);
  await ai
    .post(metricUrl, data)
    .then(() => {})
    .catch(() => {});
};



// WEBPACK FOOTER //
// ../react-lib/src/apis/pet.api.js